/**
 * Route table (spec section 3). Every page module is lazy-loaded from its
 * contracted path and named export so the shell compiles before the pages
 * exist. `RequireCompany` guards everything except /start/*. The phone pages
 * (kamera.html, dela.html) are separate entries and never enter this router.
 */
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route } from 'react-router-dom';
import { App } from '@/App';
import { AppShell } from './shell/AppShell';
import { BareLayout } from './shell/BareLayout';
import { PageSkeleton } from './components/Skeleton';
import { EmptyState } from './components/EmptyState';
import { LinkButton } from './components/LinkButton';
import { ErrorMessage } from './components/ErrorMessage';
import { useCompany } from './hooks/useCompany';

function lazyNamed<K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => loader().then((m) => ({ default: m[name] })));
}

const OnboardingRoutes = lazyNamed(() => import('./onboarding/OnboardingRoutes'), 'OnboardingRoutes');
const HomePage = lazyNamed(() => import('./home/HomePage'), 'HomePage');
const InboxPage = lazyNamed(() => import('./inbox/InboxPage'), 'InboxPage');
const ReviewPage = lazyNamed(() => import('./inbox/ReviewPage'), 'ReviewPage');
const AddRoutes = lazyNamed(() => import('./add/AddRoutes'), 'AddRoutes');
const HandelserRoutes = lazyNamed(() => import('./handelser/HandelserRoutes'), 'HandelserRoutes');
const ReportRoutes = lazyNamed(() => import('./reports/ReportRoutes'), 'ReportRoutes');
const YearEndPage = lazyNamed(() => import('./bokslut/YearEndPage'), 'YearEndPage');
const MerRoutes = lazyNamed(() => import('./mer/MerRoutes'), 'MerRoutes');

/** Route-level Suspense with a skeleton, never a spinner. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

/** Redirect to /start when no company is active, otherwise render the children. */
export function RequireCompany({ children }: { children: ReactNode }) {
  const { company, loading } = useCompany();
  if (loading) return <PageSkeleton />;
  if (!company) return <Navigate to="/start" replace />;
  return <>{children}</>;
}

/** `/` → /start without a company, else /hem. */
function RootRedirect() {
  const { company, loading } = useCompany();
  if (loading) return <PageSkeleton />;
  return <Navigate to={company ? '/hem' : '/start'} replace />;
}

function NotFound() {
  return <EmptyState title="Sidan finns inte" body="Adressen stämmer inte. Gå till Hem så hittar du vidare." actions={<LinkButton to="/hem" variant="primary">Till Hem</LinkButton>} />;
}

function RouteError() {
  return (
    <BareLayout>
      <ErrorMessage message="Sidan kunde inte visas." next="Ladda om sidan. Din bokföring är sparad lokalt." />
    </BareLayout>
  );
}

export const routes = createRoutesFromElements(
  <Route element={<App />} errorElement={<RouteError />}>
    <Route path="/" element={<RootRedirect />} />

    {/* Bare routes: no company required, no shell. */}
    <Route element={<BareLayout />}>
      <Route
        path="/start/*"
        element={
          <Lazy>
            <OnboardingRoutes />
          </Lazy>
        }
      />
    </Route>

    {/* Everything else lives inside the shell and needs an active company. */}
    <Route
      element={
        <RequireCompany>
          <AppShell />
        </RequireCompany>
      }
    >
      <Route
        path="/hem"
        element={
          <Lazy>
            <HomePage />
          </Lazy>
        }
      />
      <Route
        path="/att-gora"
        element={
          <Lazy>
            <InboxPage />
          </Lazy>
        }
      />
      <Route
        path="/att-gora/:itemId"
        element={
          <Lazy>
            <ReviewPage />
          </Lazy>
        }
      />
      <Route
        path="/lagg-till/*"
        element={
          <Lazy>
            <AddRoutes />
          </Lazy>
        }
      />
      <Route
        path="/handelser/*"
        element={
          <Lazy>
            <HandelserRoutes />
          </Lazy>
        }
      />
      <Route
        path="/rapporter/*"
        element={
          <Lazy>
            <ReportRoutes />
          </Lazy>
        }
      />
      <Route
        path="/bokslut/:fiscalYearId"
        element={
          <Lazy>
            <YearEndPage />
          </Lazy>
        }
      />
      <Route
        path="/mer/*"
        element={
          <Lazy>
            <MerRoutes />
          </Lazy>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Route>,
);

export const router = createBrowserRouter(routes);
